import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToasterService } from '@app/shared/services/toaster.service';
import { FinanceService, ResourceHistoryEntry } from '../../finance.service';

/**
 * One row in the History table. Wraps a {@link ResourceHistoryEntry} with the
 * fields the template binds to directly, flattened from the BE's snapshot map
 * for ergonomic *ngFor / sort access. Kept narrow so the BE can grow the
 * snapshot vocabulary without forcing FE shape changes.
 */
interface HistoryRow {
  startDate: string | null;
  endDate: string | null;
  hiringStatus: string | null;
  supportingLob: string | null;
  dntIndiaLeader: string | null;
  projectName: string | null;
  projectIdCode: string | null;
  allocationPct: number | null;
  capexOpex: string | null;
  poType: string | null;
  locationType: string | null;
  poiType: string | null;
  employer: string | null;
  /** Underlying changedAt — used for "Released" tinting of stale rows + tooltip. */
  changedAt: string;
  /** "Released" rows render greyed-out per Figma. */
  isReleased: boolean;
}

const PAGE_SIZE_DEFAULT = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

@Component({
  selector: 'app-finance-project-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './finance-project-history.component.html',
  styleUrl: './finance-project-history.component.scss',
})
export class FinanceProjectHistoryComponent implements OnInit, OnDestroy {
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  /** All history rows for the resource, ASC by changedAt per AB#3458256. */
  private allRows: HistoryRow[] = [];
  /** Subset of allRows after search filter — what the paginator pages over. */
  filteredRows: HistoryRow[] = [];
  /** Paginated slice rendered in the table. */
  pageRows: HistoryRow[] = [];

  searchText = '';
  pageIndex = 0;
  pageSize = PAGE_SIZE_DEFAULT;

  loading = false;
  errorMessage: string | null = null;

  resourceId!: number;
  resourceName = '';

  readonly displayedColumns: string[] = [
    'startDate',
    'endDate',
    'hiringStatus',
    'supportingLob',
    'dntIndiaLeader',
    'projectNameId',
    'allocationPct',
    'capexOpex',
    'poType',
    'locationType',
    'poiType',
    'employer',
  ];

  @ViewChild(MatPaginator) paginator?: MatPaginator;

  private readonly searchInput$ = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('resourceId'));
    if (!id || Number.isNaN(id)) {
      this.router.navigate(['/finance/details']);
      return;
    }
    this.resourceId = id;
    this.resourceName = this.route.snapshot.queryParamMap.get('name') ?? '';

    this.subs.add(
      this.searchInput$
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe(() => this.applySearchAndPaginate()),
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.financeService.listProjectHistory(this.resourceId).subscribe({
      next: (entries: ResourceHistoryEntry[]) => {
        this.allRows = entries
          // The Project History page is scoped to assignment changes per
          // Figma columns; rate-card entries are surfaced on the Rate Card
          // tab (future story).
          .filter((e) => e.entityKind === 'assignment')
          .map((e) => this.toRow(e));
        this.applySearchAndPaginate();
        this.loading = false;
      },
      error: (err) => {
        console.error('Project History load failed', err);
        this.errorMessage = 'Unable to load history. Please try again.';
        this.loading = false;
        this.toaster.showError('Failed to load history', 'Error');
      },
    });
  }

  private toRow(entry: ResourceHistoryEntry): HistoryRow {
    const s = entry.snapshot ?? {};
    const get = (key: string): string | null => {
      const v = s[key];
      if (v === null || v === undefined || v === '') return null;
      // BE snapshot values are LocalDate / String / BigDecimal — Jackson
      // serializes them to primitives, so a stringification fallback for
      // arbitrary objects shouldn't fire in practice. Guard anyway.
      if (typeof v === 'object') return null;
      return String(v);
    };
    const getNum = (key: string): number | null => {
      const v = s[key];
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'object') return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const hiringStatus = get('Hiring Status');
    return {
      startDate: get('Start Date'),
      endDate: get('End Date'),
      hiringStatus,
      supportingLob: get('Supporting LOB'),
      dntIndiaLeader: get('D&T India Leader'),
      projectName: get('Project Name') ?? entry.projectName ?? null,
      projectIdCode: get('Project ID') ?? entry.projectIdCode ?? null,
      allocationPct: getNum('Allocation'),
      capexOpex: get('CAPEX/OPEX'),
      poType: get('PO Type'),
      locationType: get('Location Type'),
      poiType: get('POI Type'),
      employer: get('Employer'),
      changedAt: entry.changedAt,
      isReleased: hiringStatus === 'RELEASED' || hiringStatus === 'RELEASED_MANUAL_ENTRY',
    };
  }

  onSearchChange(): void {
    this.searchInput$.next(this.searchText);
  }

  clearSearch(): void {
    this.searchText = '';
    this.applySearchAndPaginate();
  }

  private applySearchAndPaginate(): void {
    const needle = this.searchText.trim().toLowerCase();
    this.filteredRows = needle
      ? this.allRows.filter((r) => this.rowMatches(r, needle))
      : [...this.allRows];
    this.pageIndex = 0;
    this.recomputePageRows();
  }

  private rowMatches(row: HistoryRow, needle: string): boolean {
    const haystack = [
      row.startDate,
      row.endDate,
      this.formatHiringStatus(row.hiringStatus),
      row.supportingLob,
      row.dntIndiaLeader,
      row.projectName,
      row.projectIdCode,
      this.formatCapexOpex(row.capexOpex),
      row.poType,
      row.locationType,
      row.poiType,
      row.employer,
    ]
      .filter((v): v is string => !!v)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  }

  onPageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.recomputePageRows();
  }

  private recomputePageRows(): void {
    const start = this.pageIndex * this.pageSize;
    this.pageRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  /** "Records 1-25 of 248" footer label. */
  get pageStart(): number {
    return this.filteredRows.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }
  get pageEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredRows.length);
  }
  get totalPages(): number {
    if (this.filteredRows.length === 0) return 1;
    return Math.ceil(this.filteredRows.length / this.pageSize);
  }

  /**
   * Placeholder for the Download button on the Figma. Full export is part of
   * AB#3461223 (deferred). For now we surface a toast so the operator knows
   * it's wired but not yet shipped.
   */
  onDownload(): void {
    this.toaster.showInfo('Download is coming soon', 'Info');
  }

  backToDetails(): void {
    this.router.navigate(['/finance/details']);
  }

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  formatDate(iso: string | null): string {
    if (!iso) return '';
    // History dates arrive as ISO yyyy-MM-dd strings (assignment billing
    // dates) — render as dd/MM/yy per Figma.
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    return `${d}/${m}/${y.slice(2)}`;
  }

  formatAllocation(value: number | null): string {
    if (value === null || value === undefined) return '';
    return `${Math.round(value)}%`;
  }

  hiringStatusIcon(code: string | null): string {
    switch (code) {
      case 'JOINED':                return 'check_circle';
      case 'EXCLUDED':              return 'cancel';
      case 'RELEASED':              return 'remove_circle';
      case 'BACKED_OUT':            return 'undo';
      case 'HOLD':                  return 'pause_circle';
      case 'MANUAL_ENTRY':
      case 'RELEASED_MANUAL_ENTRY': return 'edit';
      default:                      return 'help_outline';
    }
  }

  /** CSS class for the hiring-status icon — colour matches the Figma legend. */
  hiringStatusIconClass(code: string | null): string {
    switch (code) {
      case 'JOINED':                return 'status-joined';
      case 'EXCLUDED':              return 'status-excluded';
      case 'RELEASED':              return 'status-released';
      case 'BACKED_OUT':            return 'status-backed-out';
      case 'HOLD':                  return 'status-hold';
      case 'MANUAL_ENTRY':
      case 'RELEASED_MANUAL_ENTRY': return 'status-manual';
      default:                      return '';
    }
  }

  formatHiringStatus(code: string | null): string {
    if (!code) return '';
    switch (code) {
      case 'JOINED':                 return 'Joined';
      case 'EXCLUDED':               return 'Excluded';
      case 'RELEASED':               return 'Released';
      case 'BACKED_OUT':             return 'Backed Out';
      case 'HOLD':                   return 'Hold';
      case 'MANUAL_ENTRY':           return 'Manual Entry';
      case 'RELEASED_MANUAL_ENTRY':  return 'Released — Manual';
      default:                       return code;
    }
  }

  formatCapexOpex(code: string | null): string {
    if (!code) return '';
    switch (code) {
      case 'CAPEX':     return 'CAPEX';
      case 'PROJ_OPEX': return 'OPEX';
      case 'RTB_OPEX':  return 'RTB OPEX';
      default:          return code;
    }
  }
}
